/**
 * @typedef {Object} FrontendColor
 * @property {number} colorId
 * @property {string} colorName
 * @property {string} colorHexCode
 * @property {Array<{size: string, variantId: number, stock: number, stockLevel: string}>} sizeVariants
 */

/**
 * @typedef {Object} FrontendSize
 * @property {string} size
 * @property {number[]} availableColorIds
 */

/**
 * @typedef {Object} FrontendProduct
 * @property {number} id
 * @property {number} categoryTypeId
 * @property {number} price
 * @property {number|null} oldPrice
 * @property {number|null} discount
 * @property {boolean} isNew
 * @property {boolean} isLimited
 * @property {boolean} isOnSale
 * @property {string} stockLevel
 * @property {FrontendColor[]} colors
 * @property {FrontendSize[]} sizes
 * @property {any[]} images
 * @property {any[]} variants
 */

class ProductMapper {
  /**
   * Transform backend product format to frontend format for display
   * @param {any} backendProduct - Single product from backend
   * @returns {FrontendProduct}
   */
  mapSingleProduct(backendProduct) {
    if (!backendProduct || !Array.isArray(backendProduct.variants)) {
      return this._getEmptyProduct(backendProduct?.productId)
    }

    const variants = backendProduct.variants || []
    const discount = backendProduct.discountPercentage || 0
    const isOnSale = discount > 0
    const inStock = this._calculateInStock(variants)

    const sortedImages = this._sortImagesByDisplayOrder(backendProduct.images || [])
    const colors = this._extractColors(variants, sortedImages)
    const sizes = this._extractSizes(variants, colors)
    const primaryImage = sortedImages.find(img => img.isPrimary) || sortedImages[0]
    const secondaryImage = sortedImages.find(img => img.isSecondary) || sortedImages[1] || primaryImage
    const cartPreviewImage = sortedImages.find(img => img.isCartPreview) || primaryImage

    return {
      id: backendProduct.productId,
      name: backendProduct.categoryTypeName || backendProduct.name || `Product ${backendProduct.productId}`,
      slug: backendProduct.categoryTypeSlug || null,
      productSku: backendProduct.categoryTypeSku || null,
      categoryTypeSku: backendProduct.categoryTypeSku || null,
      categoryTypeId: backendProduct.categoryTypeId || backendProduct.CategoryTypeId,
      // Nuova logica: usiamo direttamente i campi calcolati dall'API
      price: Number(backendProduct.finalPrice) || 0,
      oldPrice: isOnSale ? (Number(backendProduct.listPrice) || null) : null,
      discount: isOnSale ? Math.round(discount) : null,
      isNew: backendProduct.isNew === true,
      isLimited: backendProduct.isLimitedEdition === true,
      isActive: backendProduct.isActive !== false,
      activeAt: backendProduct.activeAt ?? backendProduct.ActiveAt ?? null,
      isOnSale: isOnSale,
      inStock: inStock,
      stockLevel: this._calculateOverallStockLevel(variants),
      createdAt: backendProduct.createdAt || null,
      shortDescription: backendProduct.shortDescription || '',
      description: backendProduct.description || '',
      descriptionEn: backendProduct.descriptionEn || '',
      descriptionIt: backendProduct.descriptionIt || '',
      careInstructionsEn: backendProduct.careInstructionsEn || '',
      careInstructionsIt: backendProduct.careInstructionsIt || '',
      fitSizing: backendProduct.fitSizing || '',
      materialComposition: backendProduct.materialComposition || '',
      colors: colors,
      sizes: sizes,
      images: sortedImages,
      variants: variants,
      primaryImage: primaryImage,
      secondaryImage: secondaryImage,
      cartPreviewImage: cartPreviewImage
    }
  }

  /**
   * Transform array of backend products for list display
   * @param {any[]} backendProducts
   * @returns {FrontendProduct[]}
   */
  mapProductsList(backendProducts) {
    if (!Array.isArray(backendProducts)) {
      return []
    }

    return backendProducts.map(product => this.mapSingleProduct(product))
  }

  /**
   * Extract unique colors from variants with their size options
   * @private
   * @param {any[]} variants
   * @returns {FrontendColor[]}
   */
  _extractColors(variants, sortedImages = []) {
    const colorMap = new Map()
    const imageColorOrder = new Map()

    if (sortedImages && sortedImages.length > 0) {
      let imageIndex = 0
      sortedImages.forEach(img => {
        const colorName = this._extractColorFromImageUrl(img.imageUrl)
        if (colorName && !imageColorOrder.has(colorName)) {
          imageColorOrder.set(colorName, imageIndex++)
        }
      })
    }

    variants.forEach(variant => {
      const colorId = variant.colorId
      
      if (!colorMap.has(colorId)) {
        let displayOrder = variant.displayOrder
        
        if (displayOrder === undefined || displayOrder === null) {
          const colorName = variant.colorName || 'Unknown'
          displayOrder = imageColorOrder.has(colorName) ? imageColorOrder.get(colorName) : Infinity
        }

        colorMap.set(colorId, {
          colorId,
          colorName: variant.colorName || 'Unknown',
          colorHexCode: variant.colorHexCode || '#000000',
          displayOrder: displayOrder,
          sizeVariants: []
        })
      } else {
        const colorEntry = colorMap.get(colorId)
        if (variant.displayOrder !== undefined && variant.displayOrder < colorEntry.displayOrder) {
          colorEntry.displayOrder = variant.displayOrder
        }
      }

      const colorEntry = colorMap.get(colorId)
      colorEntry.sizeVariants.push({
        size: variant.size || 'One Size',
        variantId: variant.variantId,
        stock: variant.stockQuantity || 0,
        stockLevel: variant.stockLevel || 'out',
        sku: variant.sku || null
      })
    })

    const colors = Array.from(colorMap.values())
    const sorted = colors.sort((a, b) => a.displayOrder - b.displayOrder)
    return sorted
  }

  _extractColorFromImageUrl(imageUrl) {
    if (!imageUrl) return null
    
    const filename = imageUrl.toLowerCase()
    
    if (filename.includes('rosso') || filename.includes('red')) return 'Red'
    if (filename.includes('arancione') || filename.includes('orange')) return 'Orange'
    if (filename.includes('grigio') || filename.includes('grey') || filename.includes('gray') || filename.includes('melange')) return 'Grey Melange'
    if (filename.includes('nero') || filename.includes('black')) return 'Black'
    if (filename.includes('bianco') || filename.includes('white')) return 'White'
    if (filename.includes('teal') || filename.includes('blu')) return 'Teal'
    
    return null
  }

  /**
   * Extract unique sizes from variants with their color options
   * @private
   * @param {any[]} variants
   * @param {FrontendColor[]} colors
   * @returns {FrontendSize[]}
   */
  _extractSizes(variants, colors) {
    const sizeSet = new Set()

    variants.forEach(variant => {
      if (variant.size) {
        sizeSet.add(variant.size)
      }
    })

    return Array.from(sizeSet).map(size => ({
      size,
      availableColorIds: colors.filter(color => 
        color.sizeVariants.some(sv => sv.size === size)
      ).map(c => c.colorId)
    }))
  }

  /**
   * Sort images by displayOrder field
   * @private
   * @param {any[]} images
   * @returns {any[]}
   */
  _sortImagesByDisplayOrder(images) {
    if (!Array.isArray(images)) return []
    
    return [...images].sort((a, b) => {
      const orderA = a.displayOrder ?? Infinity
      const orderB = b.displayOrder ?? Infinity
      return orderA - orderB
    })
  }

  /**
   * Calculate if product is in stock (at least one variant has stock > 0)
   * @private
   * @param {any[]} variants
   * @returns {boolean}
   */
  _calculateInStock(variants) {
    if (!variants || variants.length === 0) return false
    
    return variants.some(v => (v.stockQuantity || 0) > 0)
  }

  /**
   * Calculate overall stock level from variants
   * @private
   * @param {any[]} variants
   * @returns {string}
   */
  _calculateOverallStockLevel(variants) {
    if (!variants || variants.length === 0) return 'out'

    const totalStock = variants.reduce((sum, v) => sum + (v.stockQuantity || 0), 0)
    
    if (totalStock > 20) return 'high'
    if (totalStock >= 5) return 'medium'
    if (totalStock > 0) return 'low'
    return 'out'
  }

  /**
   * Get empty product placeholder
   * @private
   * @param {number} [id]
   * @returns {FrontendProduct}
   */
  _getEmptyProduct(id = 0) {
    return {
      id,
      categoryTypeId: 0,
      price: 0,
      oldPrice: null,
      discount: null,
      isNew: false,
      isLimited: false,
      isOnSale: false,
      stockLevel: 'out',
      colors: [],
      sizes: [],
      images: [],
      variants: [],
      primaryImage: null,
      secondaryImage: null,
      cartPreviewImage: null
    }
  }
}

export const productMapper = new ProductMapper()
